import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitCompare, X, Filter, Layers } from 'lucide-react';
import { useLeadFilters } from '../hooks/useLeadFilters';
import { useLeadResults } from '../hooks/useLeadResults';
import {
  usePropertyComparison,
  MIN_COMPARISON,
  MAX_COMPARISON,
} from '../hooks/usePropertyComparison';
import { useMarketStore } from '../store/useMarketStore';
import { submitBatchJob } from '../services/batchApi';
import { LeadFilterPanel } from '../components/LeadPipeline/LeadFilterPanel';
import { LeadResultsTable } from '../components/LeadPipeline/LeadResultsTable';
import { PropertyComparisonModal } from '../components/LeadPipeline/PropertyComparisonModal';
import { Breadcrumbs } from '../components/Layout/Breadcrumbs';
import type { LeadSortBy, SortOrder } from '../types/leads';

/**
 * LeadPipelinePage component
 *
 * Main page for the lead pipeline that combines the filter panel
 * and results table. Manages selection state at the page level
 * and provides property comparison functionality.
 */
export const LeadPipelinePage: React.FC = () => {
  // Filter state from URL-synced hook
  const {
    filters,
    setFilters,
    resetFilters,
    hasActiveFilters,
    savedSearches,
    savePreset,
    loadPreset,
    deletePreset,
  } = useLeadFilters();

  // Fetch leads based on filters
  const {
    leads,
    total,
    page,
    pages,
    filterCounts,
    isLoading,
    isFetching,
    error,
  } = useLeadResults(filters);

  // Property comparison state
  const {
    selectedIds,
    addToComparison,
    removeFromComparison,
    clearComparison,
    canAddMore,
    comparisonReady,
    selectionCount,
  } = usePropertyComparison();

  // Navigation + market context
  const navigate = useNavigate();
  const activeMarket = useMarketStore((s) => s.activeMarket);

  // Batch underwriting state
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);

  // Comparison modal state
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);

  // Get selected properties for comparison modal
  const selectedProperties = useMemo(() => {
    return leads.filter((lead) => selectedIds.has(lead.id));
  }, [leads, selectedIds]);

  // Handle sort change
  const handleSortChange = useCallback(
    (sortBy: LeadSortBy, sortOrder: SortOrder) => {
      setFilters({ sortBy, sortOrder });
    },
    [setFilters]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      setFilters({ page: newPage });
    },
    [setFilters]
  );

  // Handle selection change from table
  const handleSelectionChange = useCallback(
    (newSelection: Set<string>) => {
      // Enforce max selection limit
      if (newSelection.size > MAX_COMPARISON) {
        return;
      }

      // Update selection - sync with comparison hook
      newSelection.forEach((id) => {
        if (!selectedIds.has(id)) {
          addToComparison(id);
        }
      });

      selectedIds.forEach((id) => {
        if (!newSelection.has(id)) {
          removeFromComparison(id);
        }
      });
    },
    [selectedIds, addToComparison, removeFromComparison]
  );

  // Handle opening comparison modal
  const handleOpenComparison = useCallback(() => {
    if (comparisonReady) {
      setIsComparisonModalOpen(true);
    }
  }, [comparisonReady]);

  // Handle closing comparison modal
  const handleCloseComparison = useCallback(() => {
    setIsComparisonModalOpen(false);
  }, []);

  // Handle removing a property from comparison (in modal)
  const handleRemoveFromComparison = useCallback(
    (id: string) => {
      removeFromComparison(id);
      // Close modal if below minimum
      if (selectionCount - 1 < MIN_COMPARISON) {
        setIsComparisonModalOpen(false);
      }
    },
    [removeFromComparison, selectionCount]
  );

  // Handle batch underwrite submission
  const handleBatchUnderwrite = useCallback(async () => {
    if (selectionCount === 0 || isBatchSubmitting) return;
    setIsBatchSubmitting(true);
    try {
      const propertyIds = Array.from(selectedIds).map((id) => Number(id));
      const result = await submitBatchJob({
        property_ids: propertyIds,
        market_id: activeMarket.market_id,
      });
      navigate(`/batch/${result.job_id}`);
    } catch (err) {
      console.error('Batch submit failed:', err);
      setIsBatchSubmitting(false);
    }
  }, [selectionCount, isBatchSubmitting, selectedIds, activeMarket.market_id, navigate]);

  // Comparison bar message
  const comparisonBarMessage = useMemo(() => {
    if (selectionCount === 0) return null;
    if (selectionCount < MIN_COMPARISON) {
      return `Select ${MIN_COMPARISON - selectionCount} more to compare`;
    }
    if (selectionCount === MAX_COMPARISON) {
      return 'Maximum 4 properties selected';
    }
    return null;
  }, [selectionCount]);

  // Loading state
  if (isLoading && leads.length === 0) {
    return (
      <div className="flex h-full bg-slate-950 items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-500">Loading leads...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-full bg-slate-950 items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 mb-3">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-red-400">Error Loading Leads</h2>
          </div>
          <p className="text-stone-500 mb-4">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="text-gold hover:text-amber-400 text-sm font-medium transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-slate-950 relative">
      {/* Left sidebar with filters - fixed width */}
      <aside className="w-80 flex-shrink-0 border-r border-slate-700 bg-slate-900">
        <LeadFilterPanel
          filters={filters}
          setFilters={setFilters}
          resetFilters={resetFilters}
          hasActiveFilters={hasActiveFilters}
          savedSearches={savedSearches}
          savePreset={savePreset}
          loadPreset={loadPreset}
          deletePreset={deletePreset}
          filterCounts={filterCounts}
          isLoading={isLoading}
        />
      </aside>

      {/* Main content area */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-950">
        {/* Header */}
        <header className="bg-slate-900 border-b border-slate-700 px-6 py-4">
          <Breadcrumbs className="mb-3" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Filter className="w-6 h-6 text-gold" />
              <div>
                <h1 className="text-xl font-semibold text-ivory">
                  Lead Pipeline
                </h1>
                <p className="text-sm text-stone-500">
                  {isLoading ? (
                    'Loading leads...'
                  ) : (
                    <>
                      {total.toLocaleString()} lead{total !== 1 ? 's' : ''} found
                      {isFetching && !isLoading && (
                        <span className="ml-2 text-gold">(refreshing...)</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Selection info in header */}
            {selectionCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-gold font-mono text-sm">
                  {selectionCount} selected
                </span>
              </div>
            )}
          </div>
        </header>

        {/* Results table */}
        <div className="flex-1 overflow-hidden pb-16">
          <LeadResultsTable
            leads={leads}
            isLoading={isLoading}
            page={page}
            pages={pages}
            total={total}
            sortBy={filters.sortBy as LeadSortBy}
            sortOrder={filters.sortOrder}
            selectedIds={selectedIds}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onSelectionChange={handleSelectionChange}
            maxSelection={MAX_COMPARISON}
            canAddMore={canAddMore}
          />
        </div>
      </main>

      {/* Floating comparison bar */}
      {selectionCount > 0 && (
        <div className="fixed bottom-0 left-80 right-0 bg-slate-900 border-t border-slate-700 shadow-lg z-40">
          <div className="flex items-center justify-between px-6 py-3">
            {/* Left: Selection info */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <GitCompare className="w-5 h-5 text-gold" />
                <span className="font-medium text-ivory">
                  {selectionCount} propert{selectionCount === 1 ? 'y' : 'ies'} selected
                </span>
              </div>
              {comparisonBarMessage && (
                <span className="text-sm text-stone-500">
                  {comparisonBarMessage}
                </span>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {/* Clear selection button */}
              <button
                onClick={clearComparison}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-stone-500 hover:text-ivory transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>

              {/* Compare button */}
              <button
                onClick={handleOpenComparison}
                disabled={!comparisonReady}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  comparisonReady
                    ? 'bg-gold text-slate-900 hover:bg-amber-400'
                    : 'bg-slate-700 text-stone-400 cursor-not-allowed'
                }`}
              >
                <GitCompare className="w-4 h-4" />
                Compare ({selectionCount})
              </button>

              {/* Batch Underwrite button */}
              <button
                onClick={handleBatchUnderwrite}
                disabled={selectionCount === 0 || isBatchSubmitting}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  selectionCount > 0 && !isBatchSubmitting
                    ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                    : 'bg-slate-700 text-stone-400 cursor-not-allowed'
                }`}
              >
                <Layers className="w-4 h-4" />
                {isBatchSubmitting ? 'Submitting...' : `Batch Underwrite (${selectionCount})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comparison modal */}
      <PropertyComparisonModal
        open={isComparisonModalOpen}
        onClose={handleCloseComparison}
        properties={selectedProperties}
        onRemove={handleRemoveFromComparison}
      />
    </div>
  );
};

export default LeadPipelinePage;
